/**
 * The list of sessions signed in on this origin.
 *
 * This replaces the single `bayunSessionId` cookie, which could only ever name one session, and the
 * `localStorageEncryptionMode` cookie, which forced every session on the origin to share one
 * storage mode.
 *
 * The registry holds no secrets — no tokens, no keys. It holds what is needed to find a session's
 * storage (its mode) and to describe it in an account switcher.
 */

import { LocalDataEncryptionMode } from "../constants/Enums";
import {
  clearSessionScope,
  readRegistryRaw,
  readSessionValueAnyArea,
  removeRegistryRaw,
  writeRegistryRaw,
} from "./SessionStorage";
import { STATE_KEY, getActiveAppId } from "./StorageKeys";

/**
 * How many accounts may be signed in to one app at once. Per app, not per origin, so one app's
 * accounts never evict another's.
 *
 * Five rather than ten: each signed-in account keeps its storage key and decrypted private keys
 * resident for the life of the page. Raising this later is easy; lowering it is not.
 */
export const MAX_SIGNED_IN_SESSIONS = 5;

/**
 * Reads the registry, tolerating a corrupt or hand-edited value.
 *
 * A registry that fails to parse is treated as empty rather than thrown from: it is derived state,
 * and refusing to start because of it would lock the user out of an app whose real session data is
 * still intact.
 */
const readRegistry = () => {
  let raw;
  try {
    raw = readRegistryRaw();
  } catch (error) {
    return {};
  }
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    console.error("Session registry is unreadable and is being reset.");
    return {};
  }
};

const writeRegistry = (registry) => {
  try {
    writeRegistryRaw(JSON.stringify(registry));
  } catch (error) {
    console.error("Could not persist the session registry", error);
  }
};

/**
 * The key that names this session to the server, learned from the X-Bayun-Account response header
 * when the session was established.
 *
 * The auth cookie names the browser's bundle; this names the account inside it. Without it the
 * server cannot tell which of the signed-in accounts a request is for, and answers none of them.
 */
export const getAccountKey = (sessionId) => {
  const record = getSessionRecord(sessionId);
  return record && record.accountKey ? record.accountKey : "";
};

export const setAccountKey = (sessionId, accountKey) => {
  if (!sessionId || !accountKey || getAccountKey(sessionId) === accountKey) {
    return;
  }
  upsertSessionRecord(sessionId, { accountKey });
};

export const getSessionRecord = (sessionId) => {
  if (!sessionId) {
    return null;
  }
  const record = readRegistry()[sessionId];
  return record || null;
};

export const hasSessionRecord = (sessionId) => getSessionRecord(sessionId) != null;

/**
 * The storage mode for a session.
 *
 * Defaults to SESSION_MODE for an unknown session, matching the old cookie behaviour: a missing
 * mode cookie also read as "not EXPLICIT_LOGOUT_MODE".
 */
export const getSessionMode = (sessionId) => {
  const record = getSessionRecord(sessionId);
  return record ? record.mode : LocalDataEncryptionMode.SESSION_MODE;
};

/**
 * Creates or updates a session's record.
 *
 * Read-modify-write against localStorage. Two tabs completing a login in the same instant can
 * therefore lose one entry; the loss is a registry row, not key material, and the affected tab
 * recovers by signing in again. A cross-tab lock is deliberately not used here because the SDK's
 * storage calls are synchronous and navigator.locks is not.
 */
export const upsertSessionRecord = (sessionId, patch = {}) => {
  if (!sessionId) {
    throw new Error("sessionId is required to record a session.");
  }
  const registry = readRegistry();
  const existing = registry[sessionId] || {};
  const record = {
    appId: getActiveAppId(),
    mode: LocalDataEncryptionMode.SESSION_MODE,
    ...existing,
    ...patch,
    lastActive: Date.now(),
  };
  registry[sessionId] = record;
  writeRegistry(registry);
  return record;
};

export const touchSession = (sessionId) => {
  if (!getSessionRecord(sessionId)) {
    return null;
  }
  return upsertSessionRecord(sessionId, {});
};

export const removeSessionRecord = (sessionId) => {
  if (!sessionId) {
    return;
  }
  const registry = readRegistry();
  if (!(sessionId in registry)) {
    return;
  }
  delete registry[sessionId];
  writeRegistry(registry);
};

/**
 * Every session in the registry, whichever app it belongs to, most recently active first.
 *
 * Rarely what a caller wants — see listSessions.
 */
export const listAllSessions = () => {
  const registry = readRegistry();
  return Object.keys(registry)
    .map((sessionId) => ({ sessionId, ...registry[sessionId] }))
    .sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
};

/**
 * The sessions belonging to the app that is running, most recently active first.
 *
 * The registry is one object per origin, so two Bayun apps served from the same origin share it.
 * Their stored *values* are already separated by the appId in every key, but the session list is
 * not — so without this filter an app's account switcher would offer the other app's accounts, and
 * a fresh tab could adopt a session belonging to the other app and then find no state under its own
 * appId, appearing signed out.
 */
export const listSessions = () => {
  const appId = getActiveAppId();
  return listAllSessions().filter((session) => (session.appId || "") === appId);
};

/**
 * How long a record with no account key is left alone before it counts as abandoned. A sign-in in
 * another tab has a record before it has a key, and reaping that would break it mid-login.
 */
const STUB_GRACE_MS = 10 * 60 * 1000;

/**
 * How long a record that has an account key but never finished signing in is kept. Longer than the
 * stub grace, because a slow signup — typing security questions — sits in exactly this state.
 */
const UNFINISHED_GRACE_MS = 24 * 60 * 60 * 1000;

const hasReadableState = (sessionId) => {
  try {
    return readSessionValueAnyArea(sessionId, STATE_KEY) != null;
  } catch (error) {
    return false;
  }
};

/**
 * Records that a session finished signing in.
 *
 * Every login creates a registry record before any credential is checked, so a record alone does
 * not mean an account. This stamp is what does: the account switcher lists only stamped sessions,
 * and only stamped sessions count toward the cap. Set by the SDK when an authentication succeeds.
 */
export const markSignedIn = (sessionId) => {
  if (!sessionId) {
    return;
  }
  const record = getSessionRecord(sessionId);
  if (record && record.signedInAt) {
    return;
  }
  upsertSessionRecord(sessionId, { signedInAt: Date.now() });
};

/**
 * A finished sign-in whose state this page can read — an account this tab can actually act as.
 *
 * A SESSION_MODE session belonging to another tab fails the last test, deliberately. Its state is
 * in that tab's sessionStorage, so switching to it here would only produce a broken session, and
 * evicting it from here would drop its record while leaving that tab's storage behind.
 */
const isResident = (session) =>
  Boolean(session && session.signedInAt && session.accountKey) &&
  hasReadableState(session.sessionId);

/**
 * The accounts this tab can switch to, most recently active first. What the account switcher shows.
 */
export const listSignedInSessions = () => listSessions().filter(isResident);

/**
 * A record left by a sign-in that never finished, old enough that no tab is still working on it.
 *
 * Every login creates a record before checking any credential, so a mistyped password leaves one
 * behind. These are not accounts, and counting them once let five failed attempts evict — and log
 * out — the account the user was actually using.
 */
const isAbandonedSignIn = (session) => {
  if (session.signedInAt) {
    return false;
  }
  const grace = session.accountKey ? UNFINISHED_GRACE_MS : STUB_GRACE_MS;
  return Date.now() - (session.lastActive || 0) > grace;
};

/**
 * Drops the records of sign-ins that never finished, along with anything they wrote. No server
 * call: an unfinished session's server row expires on its own.
 *
 * @returns {String[]} the sessionIds that were reaped.
 */
export const reapAbandonedSessions = () => {
  const reaped = listSessions().filter(isAbandonedSignIn).map(({ sessionId }) => sessionId);
  reaped.forEach((sessionId) => {
    try {
      clearSessionScope(sessionId);
    } catch (error) {
      console.error("Could not clear storage for abandoned sign-in", error);
    }
    removeSessionRecord(sessionId);
  });
  return reaped;
};

/**
 * Which sessions are over the cap, least recently active first, without dropping them. Counts only
 * resident sessions; see isResident.
 *
 * Exported so a caller can end them on the server first — which has to happen before eviction,
 * because the request needs the account key and stored appId that eviction removes.
 *
 * @returns {String[]} sessionIds that exceed the cap.
 */
export const sessionsOverCap = (cap = MAX_SIGNED_IN_SESSIONS) => {
  const resident = listSignedInSessions();
  if (resident.length <= cap) {
    return [];
  }
  return resident.slice(cap).map(({ sessionId }) => sessionId);
};

/**
 * Drops the least recently active sessions until the cap is met, clearing their stored data.
 *
 * Local teardown only — ending the server row needs an HTTP call, which does not belong in the
 * storage layer and has to happen before this runs. initAuthenticationProcess does it first.
 *
 * @returns {String[]} the sessionIds that were evicted.
 */
export const enforceSessionCap = (cap = MAX_SIGNED_IN_SESSIONS) => {
  const evicted = sessionsOverCap(cap);
  evicted.forEach((sessionId) => {
    try {
      clearSessionScope(sessionId);
    } catch (error) {
      console.error("Could not clear storage for evicted session", error);
    }
    removeSessionRecord(sessionId);
  });
  return evicted;
};

/** Removes every record. Used by logout-all; does not touch the sessions' stored data. */
export const clearRegistry = () => {
  try {
    removeRegistryRaw();
  } catch (error) {
    console.error("Could not clear the session registry", error);
  }
};
